import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../../contexts/AuthContext";
import { parseAuthLinkError } from "../../../hooks/auth/authLinkError";
import { getErrorMessage, getPostAuthRedirect, getSupabaseErrorMessage } from "../../../utils";
import { AuthLayout } from "../AuthLayout";
import { SignInView } from "./SignInView";

type AuthMode = "signin" | "forgotPassword";

export const SignIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, resetPasswordForEmail, signIn } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<AuthMode>("signin");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [sessionError, setSessionError] = useState<string | undefined>(
    location.state?.errorMessage ?? parseAuthLinkError(location.hash) ?? undefined
  );

  const { redirectPath } = useMemo(() => getPostAuthRedirect({ location }), [location]);

  const forgotPasswordAction = async () => {
    try {
      const response = await resetPasswordForEmail(email);
      toast.success(response.message);
      setTimeout(() => {
        setMode("signin");
      }, 2000);
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const signInAction = async () => {
    try {
      const response = await signIn({ email, password });
      if (response.error) {
        if (response.error.message === "Email not confirmed") {
          navigate("/email-confirmation", { replace: true, state: { email } });
        }
        throw response.error;
      }

      if (!response.user) {
        throw new Error("Authentication failed: No user returned");
      }

      navigate(redirectPath, { replace: true });
    } catch (error) {
      setError(getSupabaseErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setSessionError(undefined);
    setIsLoading(true);

    if (mode === "forgotPassword") {
      await forgotPasswordAction();
    } else {
      await signInAction();
    }
  };

  if (user) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <AuthLayout>
      <SignInView
        email={email}
        password={password}
        mode={mode}
        isLoading={isLoading}
        error={error}
        sessionError={sessionError}
        setError={setError}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        onModeChange={setMode}
        onSubmit={handleSubmit}
      />
    </AuthLayout>
  );
};
